import { ButtonHTMLAttributes, forwardRef } from 'react';
import { VariantProps, cva } from 'class-variance-authority';
import Icon, { IconProps } from '@/components/ui/icon';
import Spinner, { SpinnerProps } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

export type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof iconButtonVariants> & {
    isLoading?: boolean;
    icon: string;
    iconFill?: boolean;
    variant?: 'outlined' | 'borderless';
    shape?: 'rounded' | 'squared';
    color?: 'achromatic' | 'achromatic-primary' | 'achromatic-info' | 'primary' | 'info';
    size?: '3xs' | '2xs' | 'xs' | 'sm' | 'md';
  };

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      type = 'button',
      isLoading = false,
      icon,
      iconFill,
      variant = 'borderless',
      shape = 'rounded',
      color = 'primary',
      size = 'md',
      disabled,
      className,
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      // eslint-disable-next-line react/button-has-type
      type={type}
      disabled={disabled}
      className={cn(iconButtonVariants({ variant, shape, color, size }), className)}
      {...props}>
      {isLoading ? (
        <Spinner
          color={disabled ? 'achromatic' : color}
          size={iconButtonSpinnerSizeMap[size] as SpinnerProps['size']}
        />
      ) : (
        <Icon
          name={icon}
          fill={iconFill}
          size={iconButtonIconSizeMap[size] || 24}
          className="text-inherit"
        />
      )}
    </button>
  )
);

IconButton.displayName = 'IconButton';

const iconButtonIconSizeMap: Record<
  Exclude<IconButtonProps['size'], undefined>,
  Exclude<IconProps['size'], undefined>
> = {
  '3xs': 12,
  '2xs': 14,
  xs: 16,
  sm: 18,
  md: 24,
};
const iconButtonSpinnerSizeMap: Record<
  Exclude<IconButtonProps['size'], undefined>,
  Exclude<SpinnerProps['size'], undefined>
> = {
  '3xs': '4xs',
  '2xs': '3xs',
  xs: '2xs',
  sm: 'xs',
  md: 'sm',
};

const iconButtonVariants = cva(
  [
    'flex',
    'justify-center',
    'items-center',
    'disabled:text-achromatic-disabled-dark',
    'disabled:cursor-not-allowed',
    'transition-all',
  ],
  {
    variants: {
      variant: {
        borderless: 'disabled:bg-transparent',
        outlined: [
          'border',
          'disabled:border-achromatic-disabled-dark',
          'disabled:bg-achromatic-disabled-light',
        ],
      },
      shape: {
        rounded: 'rounded-full',
        squared: 'rounded-5',
      },
      size: {
        '3xs': ['w-4', 'h-4'],
        '2xs': ['w-5', 'h-5'],
        xs: ['w-6', 'h-6'],
        sm: ['w-[1.875rem]', 'h-[1.875rem]'],
        md: ['w-9', 'h-9'],
      },
      color: {
        achromatic: [
          'text-achromatic-light',
          'hover:bg-achromatic-hover',
          'active:text-achromatic-80',
          'active:bg-achromatic-active',
          'data-[state=active]:text-achromatic-80',
          'data-[state=active]:bg-achromatic-active',
          'data-[state=open]:text-achromatic-80',
          'data-[state=open]:bg-achromatic-active',
        ],
        'achromatic-primary': [
          'text-achromatic-light',
          'hover:text-primary-light',
          'hover:bg-primary-hover',
          'active:text-primary-dark',
          'active:bg-primary-light-20',
          'data-[state=active]:text-primary-dark',
          'data-[state=active]:bg-primary-light-20',
          'data-[state=open]:text-primary-dark',
          'data-[state=open]:bg-primary-light-20',
        ],
        'achromatic-info': [
          'text-achromatic-light',
          'hover:text-info-light',
          'hover:bg-info-hover',
          'active:text-info-dark',
          'active:bg-info-light-20',
          'data-[state=active]:text-info-dark',
          'data-[state=active]:bg-info-light-20',
          'data-[state=open]:text-info-dark',
          'data-[state=open]:bg-info-light-20',
        ],
        primary: [
          'text-primary',
          'hover:bg-primary-hover',
          'active:text-primary-dark',
          'active:bg-primary-light-20',
          'data-[state=active]:text-primary-dark',
          'data-[state=active]:bg-primary-light-20',
          'data-[state=open]:text-primary-dark',
          'data-[state=open]:bg-primary-light-20',
        ],
        info: [
          'text-info',
          'hover:bg-info-hover',
          'active:text-info-dark',
          'active:bg-info-light-20',
          'data-[state=active]:text-info-dark',
          'data-[state=active]:bg-info-light-20',
          'data-[state=open]:text-info-dark',
          'data-[state=open]:bg-info-light-20',
        ],
      },
    },
    compoundVariants: [
      {
        variant: 'outlined',
        color: 'achromatic',
        className: ['border-achromatic-light'],
      },
      {
        variant: 'outlined',
        color: 'achromatic-primary',
        className: [
          'border-achromatic-light',
          'hover:border-primary',
          'active:border-primary',
          'data-[state=active]:border-primary',
          'data-[state=open]:border-primary',
        ],
      },
      {
        variant: 'outlined',
        color: 'achromatic-info',
        className: [
          'border-achromatic-light',
          'hover:border-info',
          'active:border-info',
          'data-[state=active]:border-info',
          'data-[state=open]:border-info',
        ],
      },
      { variant: 'outlined', color: 'primary', className: ['border-primary'] },
      { variant: 'outlined', color: 'info', className: ['border-info'] },
    ],
  }
);

export default IconButton;
